import {RefObject, useEffect, useState} from 'react';
// eslint-disable-next-line import/default
import ReactQuill from 'react-quill-new';

import {MAX_COMMENT_HTML_LENGTH, MAX_COMMENT_TEXT_LENGTH} from 'src/modules/comments/constants';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

export const useQuillLength = (
    quillRef: RefObject<ReactQuill | null>,
    onHtmlLengthChange: (length: number) => void,
    onTextLengthChange: (length: number) => void,
): number => {
    const {onCreateNote} = usePushNotification();
    const [textLength, setTextLength] = useState(0);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();

        if (!quill) {
            return;
        }

        const handler = () => {
            const text = quill.getText().trim();
            const html = quill.root.innerHTML ?? '';
            const currentTextLength = text.length;
            const currentHtmlLength = html.length;

            setTextLength(currentTextLength);
            onTextLengthChange(currentTextLength);
            onHtmlLengthChange(currentHtmlLength);

            if (currentTextLength > MAX_COMMENT_TEXT_LENGTH) {
                quill.deleteText(MAX_COMMENT_TEXT_LENGTH, currentTextLength);
            }

            if (currentHtmlLength > MAX_COMMENT_HTML_LENGTH) {
                onCreateNote({
                    id: 'comment-html-length-warning',
                    status: Status.WARNING,
                    message: 'Comment has too much formatting. Please reduce the amount of formatting to proceed.',
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: 10000,
                });
            }
        };

        handler();

        quill.on('text-change', handler);

        return () => {
            quill.off('text-change', handler);
        };
    }, [quillRef, onHtmlLengthChange, onTextLengthChange, onCreateNote]);

    return textLength;
};
